import {INestedValue} from "./unflattenRecord.interface";

/**
 * Converts a flat record with dot-separated keys into a nested object structure.
 *
 * @param {Record<string, string>} record - The flat record where keys are dot-separated strings.
 * @returns {INestedValue} - The resulting nested object structure.
 *
 * Example:
 * Input: { "a.b.c": "value", "x.y": "another" }
 * Output: { a: { b: { c: "value" } }, x: { y: "another" } }
 */
export default function unflattenRecord(record: Record<string, string>): INestedValue {
  const result: INestedValue = {};

  // Iterate over each key-value pair in the flat record
  Object.entries(record).forEach(([flatKey, value]) => {
    // Split the flat key into an array of keys
    const keys = flatKey.split('.');
    let currentLevel = result;

    // Traverse or create the nested structure
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        // If it's the last key, assign the value
        currentLevel[key] = value;
      } else {
        // If the key doesn't exist or isn't an object, initialize it as an object
        if (!currentLevel[key] || typeof currentLevel[key] !== 'object') {
          currentLevel[key] = {};
        }
        // Move to the next level in the nested structure
        currentLevel = currentLevel[key] as INestedValue;
      }
    });
  });

  return result;
}
